/**
 * Step Score Card Component
 *
 * Reusable component for scoring individual SOP steps
 * Features:
 * - Preset score buttons (0, 50, 75, 100)
 * - Auto pass/fail toggle at threshold (≥70 = pass)
 * - Optional feedback textarea
 * - Safety-critical highlighting
 */

import { memo, useState, useCallback } from 'react';
import {
  Check,
  X,
  ShieldAlert,
  MessageSquare,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import type { StepScore } from '../NewAssessmentPage';

interface StepScoreCardProps {
  stepId: string;
  stepNumber: number;
  description: string;
  isSafetyCritical: boolean;
  score: StepScore | undefined;
  onChange: (stepId: string, score: StepScore) => void;
}

const PASS_THRESHOLD = 70;

const PRESET_SCORES = [
  { value: 0, label: '0', color: 'bg-red-500' },
  { value: 50, label: '50', color: 'bg-amber-500' },
  { value: 75, label: '75', color: 'bg-[#c5e8c2]' },
  { value: 100, label: '100', color: 'bg-[#00674a]' },
];

export const StepScoreCard = memo(function StepScoreCard({
  stepId,
  stepNumber,
  description,
  isSafetyCritical,
  score,
  onChange,
}: StepScoreCardProps) {
  const [showFeedback, setShowFeedback] = useState(false);

  const currentScore = score?.score ?? -1;
  const isPassed = score?.passed ?? false;
  const isScored = score !== undefined;

  const handleScoreChange = useCallback((newScore: number) => {
    const passed = newScore >= PASS_THRESHOLD;
    onChange(stepId, {
      passed,
      score: newScore,
      feedback: score?.feedback ?? '',
    });
  }, [stepId, onChange, score?.feedback]);

  const handlePassToggle = useCallback(() => {
    const newPassed = !isPassed;
    onChange(stepId, {
      passed: newPassed,
      score: currentScore >= 0 ? currentScore : (newPassed ? 80 : 50),
      feedback: score?.feedback ?? '',
    });
  }, [stepId, onChange, isPassed, currentScore, score?.feedback]);

  const handleFeedbackChange = useCallback((feedback: string) => {
    onChange(stepId, {
      passed: score?.passed ?? false,
      score: score?.score ?? 0,
      feedback,
    });
  }, [stepId, onChange, score?.passed, score?.score]);

  const handleSliderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newScore = parseInt(e.target.value, 10);
    const passed = newScore >= PASS_THRESHOLD;
    onChange(stepId, {
      passed,
      score: newScore,
      feedback: score?.feedback ?? '',
    });
  }, [stepId, onChange, score?.feedback]);

  return (
    <div
      className={`
        group relative rounded-xl border transition-all duration-300
        ${isSafetyCritical
          ? 'border-red-200 bg-red-50/30 hover:border-red-300'
          : isScored
          ? 'border-[#c5e8c2] bg-[#c5e8c2]/5 hover:border-[#00674a]/40'
          : 'border-gray-200 bg-white hover:border-gray-300'
        }
      `}
    >
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* Step number */}
          <div className={`
            flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm
            ${isSafetyCritical
              ? 'bg-red-100 text-red-700'
              : isScored
              ? 'bg-[#00674a] text-white'
              : 'bg-gray-100 text-gray-600'
            }
          `}>
            {stepNumber}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 space-y-3">
            {/* Description */}
            <div className="flex items-start gap-2">
              <p className="text-sm text-gray-700 leading-relaxed flex-1">
                {description}
              </p>
              {isSafetyCritical && (
                <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-semibold">
                  <ShieldAlert className="w-3 h-3" />
                  Safety
                </span>
              )}
            </div>

            {/* Scoring controls */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Preset score buttons */}
              <div className="flex items-center gap-1">
                {PRESET_SCORES.map((preset) => (
                  <button
                    key={preset.value}
                    onClick={() => handleScoreChange(preset.value)}
                    className={`
                      w-10 h-8 rounded-lg text-xs font-bold transition-all
                      ${currentScore === preset.value
                        ? `${preset.color} text-white shadow-sm scale-105`
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }
                    `}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              {/* Divider */}
              <div className="w-px h-6 bg-gray-200" />

              {/* Pass/Fail toggle */}
              <button
                onClick={handlePassToggle}
                disabled={!isScored}
                className={`
                  inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all
                  disabled:opacity-50 disabled:cursor-not-allowed
                  ${isPassed
                    ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                    : isScored
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : 'bg-gray-100 text-gray-500'
                  }
                `}
              >
                {isPassed ? (
                  <>
                    <Check className="w-3.5 h-3.5" />
                    Pass
                  </>
                ) : (
                  <>
                    <X className="w-3.5 h-3.5" />
                    Fail
                  </>
                )}
              </button>

              {/* Feedback toggle */}
              <button
                onClick={() => setShowFeedback(!showFeedback)}
                className={`
                  inline-flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-all
                  ${showFeedback || score?.feedback
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                  }
                `}
              >
                <MessageSquare className="w-3.5 h-3.5" />
                {showFeedback ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>

              {/* Score display */}
              {isScored && (
                <div className="ml-auto">
                  <span className={`
                    text-lg font-bold
                    ${currentScore >= 80 ? 'text-emerald-600' : currentScore >= 60 ? 'text-amber-600' : 'text-red-600'}
                  `}>
                    {currentScore}
                  </span>
                  <span className="text-xs text-gray-400 ml-1">/100</span>
                </div>
              )}
            </div>

            {/* Score slider (shown when scored) */}
            {isScored && (
              <div className="pt-2">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={currentScore}
                  onChange={handleSliderChange}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-guild"
                  style={{
                    background: `linear-gradient(to right, #00674a ${currentScore}%, #e5e7eb ${currentScore}%)`,
                  }}
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>0</span>
                  <span className="text-[#00674a] font-semibold">Pass: ≥70</span>
                  <span>100</span>
                </div>
              </div>
            )}

            {/* Feedback textarea */}
            {showFeedback && (
              <div className="pt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                <textarea
                  value={score?.feedback ?? ''}
                  onChange={(e) => handleFeedbackChange(e.target.value)}
                  placeholder="Add feedback or notes for this step..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none transition-all resize-none bg-white"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scored indicator line */}
      {isScored && (
        <div className={`
          absolute left-0 top-0 bottom-0 w-1 rounded-l-xl
          ${isPassed ? 'bg-emerald-500' : 'bg-red-500'}
        `} />
      )}
    </div>
  );
});
